import { useEffect } from 'react';
import { TGetStateResponseCompanyDE } from 'lendingApi/lapiOverrides.types';
import {
  fetchGetCompanyLegalForm,
  fetchGetCompanySearch,
} from 'lendingApi/lendingApi';
import {
  TDECompanySearchResponse,
  TCompanySearchResultDE,
} from 'lendingApi/lendingApi.types';
import { useFormContext } from 'react-hook-form';
import { TSignupFormDataDE } from '../../NewSignupFormDE.types';

export const getCompanies = async (searchQuery: string) => {
  try {
    const response = await fetchGetCompanySearch<TDECompanySearchResponse>({
      companyName: searchQuery,
    });
    return response.data;
  } catch {
    return [];
  }
};

export const getLegalForm = async (crefoId: string) => {
  try {
    const response = await fetchGetCompanyLegalForm({
      crefoId,
    });
    return response.data.legal_form;
  } catch {
    return null;
  }
};

export const useCompanyInput = () => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<TSignupFormDataDE>();

  const company = watch('company');

  /**
   * Bug fix for company search. Because the Search component doesn't map input fields to react-hook-form,
   * their initial values are undefined. Therefore, they need to be initialized to empty strings here.
   */
  useEffect(() => {
    const companyDetailsFields = [
      'name',
      'crefoId',
      'street',
      'postcode',
      'town',
      'buildingNumber',
      'legalForm',
    ];

    if (company) {
      for (const key of companyDetailsFields) {
        if (typeof company[key as keyof typeof company] === 'undefined') {
          setValue(`company.${key}` as any, '');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const updateCompanyDetailsFromLookup = (
    value: TCompanySearchResultDE,
    legalForm?: TGetStateResponseCompanyDE['type'] | null,
  ) => {
    setValue('company.name', value.name);
    setValue('company.crefoId', value.crefo_id);
    setValue('company.buildingNumber', value.house_number);
    setValue('company.street', value.street);
    setValue('company.postcode', value.postcode);
    setValue('company.town', value.city);
    setValue('company.legalForm', legalForm || undefined);
  };

  const resetCompanyDetails = () => {
    setValue('company.name', '');
    setValue('company.crefoId', '');
    setValue('company.buildingNumber', '');
    setValue('company.street', '');
    setValue('company.postcode', '');
    setValue('company.town', '');
    setValue('company.legalForm', undefined);
  };

  const denormaliseSelectedCompany = (): undefined | TCompanySearchResultDE => {
    if (!watch('company.crefoId')) {
      return undefined;
    }
    return {
      house_number: company.buildingNumber,
      name: company.name,
      crefo_id: company.crefoId,
      street: company.street,
      postcode: company.postcode,
      legalForm: company.legalForm,
      city: company.town,
    };
  };

  const isManualInput = watch('company.isManualInput');

  return {
    isManualInput,
    toggleManualInput: () => setValue('company.isManualInput', !isManualInput),
    updateCompanyDetailsFromLookup,
    resetCompanyDetails,
    denormalisedSelectedCompany: denormaliseSelectedCompany(),
    lookupError: errors?.company?.crefoId?.message,
  };
};
